import { randomBytes } from '@noble/hashes/utils'
import { CRYPTO_CONSTANTS } from './constants.js'
import { decrypt, encrypt } from './crypto.js'
import type { InterWorkerMessage, RequestMessageData, ResponseMessageData } from './types.js'
import { bytesToHex, hexToBytes, textDecoder, textEncoder } from './utils.js'

/**
 * Packs a message for inter-worker communication with end-to-end encryption
 */
export const packInterWorkerMessage = async (
  privKey: Uint8Array,
  {
    publicKey,
    receiver_public_key,
    expire_at,
    data,
  }: {
    publicKey: Uint8Array
    receiver_public_key: string
    expire_at: string
    data: RequestMessageData | ResponseMessageData
  },
): Promise<InterWorkerMessage> => {
  const dataBytes = textEncoder.encode(JSON.stringify(data))
  const receiverPublicKeyBytes = hexToBytes(receiver_public_key)
  const salt = randomBytes(CRYPTO_CONSTANTS.SALT_LENGTH)
  const ciphertext = await encrypt(privKey, receiverPublicKeyBytes, dataBytes, salt)

  return {
    sender_public_key: bytesToHex(publicKey),
    receiver_public_key,
    expire_at,
    data: bytesToHex(ciphertext),
    salt: bytesToHex(salt),
  }
}

/**
 * Unpacks and decrypts an inter-worker message
 */
export const unpackInterWorkerMessageData = async (
  privKey: Uint8Array,
  { sender_public_key, data, salt }: { sender_public_key: string; data: string; salt: string },
): Promise<RequestMessageData | ResponseMessageData> => {
  const ciphertextBytes = hexToBytes(data)
  const saltBytes = hexToBytes(salt)
  const senderPublicKeyBytes = hexToBytes(sender_public_key)

  const plaintextBytes = await decrypt(privKey, senderPublicKeyBytes, ciphertextBytes, saltBytes)

  return JSON.parse(textDecoder.decode(plaintextBytes))
}
